import { calculateRemain } from './calculate_remaining.js'
export { calculateRemain } from './calculate_remaining.js'
import Evaporate from 'evaporate'
import sparkMD5 from 'spark-md5'
import sha256 from 'js-sha256'

export function createUploader(
  signerUrl,
  accessKey,
  bucket,
  onlyRetryForSameFileName = false
) {
  // Evaporate persists multipart state in localStorage under "awsUploads". On resume it
  // overwrites the form-issued S3 key with the cached awsKey. s3FileCacheHoursAgo: 0 only
  // skips *completed* entries (unfinished ones treat missing completedAt as FAR_FUTURE and
  // always pass the window check), so a stale key from a previous form token still gets
  // promoted and fails with "Invalid S3 upload location" (ENG-31154 / ENG-31138).
  clearEvaporateUploadCache()

  const obj = Evaporate.create({
    signerUrl: signerUrl,
    // Send the full canonical request to the signer so the server can verify it targets a
    // server-issued temp upload key before signing it, instead of signing a blind string
    // (ENG-31064). All upload paths go through this one factory, so this covers them all.
    sendCanonicalRequestToSignerUrl: true,
    aws_key: accessKey,
    bucket: bucket,
    cloudfront: true,
    computeContentMd5: true,
    cryptoMd5Method: (d) => btoa(sparkMD5.ArrayBuffer.hash(d, true)),
    cryptoHexEncodedHash256: sha256,
    partSize: 20 * 1024 * 1024,
    s3Acceleration: true,
    // Do not reuse a previous S3 object key for the same file content. Promote
    // validates that the key is a server-issued temp location (signed token after
    // ENG-31065); a cached key from an older form/token (or pre-HMAC deploy)
    // fails with "Invalid S3 upload location" (ENG-31138 / SIMPLERO-13CE).
    // Note: 0 is falsy in Evaporate (`hours || -100`), which disables completed-entry
    // reuse via a future HOURS_AGO cutoff — intentional. Unfinished entries are cleared
    // above via clearEvaporateUploadCache.
    s3FileCacheHoursAgo: 0,
    onlyRetryForSameFileName,
    timeUrl: '/dropzone/assets/server_time',
  })
  return obj
}

// Drop Evaporate's localStorage multipart/resume cache so uploads use only the
// server-issued temp key from the hidden #key field. Once per page load so a second
// createUploader() on the same page (dialog + page header) does not wipe in-flight
// resume metadata the first instance may have written after the initial clear.
let evaporateUploadCacheCleared = false

export function clearEvaporateUploadCache() {
  if (evaporateUploadCacheCleared) return
  evaporateUploadCacheCleared = true
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.removeItem('awsUploads')
    }
  } catch (_e) {
    // Private mode / disabled storage — nothing to clear.
  }
}

export function getFileMeta(file) {
  return new Promise(function (resolve) {
    let meta = {
      name: file.name,
      size: file.size,
      type: file.type,
      errors: [],
    }

    // No other validation matters if the file is empty.
    if (file.size == 0) {
      meta['errors'].push('The file is empty.')
      resolve(meta)
      return
    }

    if (file.type.match(/^image\//)) {
      getImageDimensions(file).then(function ([width, height, error]) {
        meta['width'] = width
        meta['height'] = height
        if (error) meta['errors'].push(error)
        resolve(meta)
      })
    } else {
      resolve(meta)
    }
  })
}

function getImageDimensions(file) {
  return new Promise(function (resolve) {
    // HEIC/HEIF files are not supported by browser's Image object but are valid image files
    // that the backend can process, so skip dimension validation for these formats
    const isHeicHeif =
      /\.(heic|heif)$/i.test(file.name) ||
      ['image/heic', 'image/heif'].includes(file.type)

    if (isHeicHeif) {
      resolve([null, null, null]) // No dimensions needed for HEIC/HEIF
      return
    }

    let img = new Image()
    img.onload = function () {
      resolve([this.width, this.height, null])
    }
    img.onerror = function () {
      resolve([null, null, 'Image does not seem to be valid.'])
    }
    img.src = URL.createObjectURL(file)
  })
}

export function deleteAssetFromDB(assetId) {
  // Import the helper function dynamically to avoid circular imports
  return import('~/common/helpers/csrf').then(({ getCsrfParamsAsJSON }) => {
    return fetch(`/dropzone/assets/${assetId}`, {
      method: 'DELETE',
      body: getCsrfParamsAsJSON(),
      headers: {
        'Content-Type': 'application/json',
      },
    }).then((response) => {
      if (!response.ok) {
        throw new Error(`Failed to delete asset: ${response.statusText}`)
      }
      return response.json()
    })
  })
}

export function createAWSUploadHandler(config) {
  const {
    signerUrl,
    awsAccessKeyId,
    bucket,
    path,
    objectName,
    onProgress,
    onComplete,
    onError,
    onCancel,
    uploadUrl = '/dropzone/assets/upload',
  } = config

  return {
    uploadFile: async (file) => {
      const uploader = createUploader(signerUrl, awsAccessKeyId, bucket)
      const fullPath = path.replaceAll('${filename}', file.name)
      const uploadStartedAt = new Date()

      try {
        const [evaporate, fileMeta] = await Promise.all([
          uploader,
          getFileMeta(file),
        ])

        if (fileMeta.errors.length > 0) {
          console.log('Validation failed: ' + fileMeta.errors)
          onError?.(
            'We could not upload your file. ' + fileMeta.errors.join(' '),
            file
          )
          return
        }

        const uploadPromise = evaporate.add({
          file: file,
          name: fullPath,
          complete: (xhr, awsObjectKey) => {
            handleUploadComplete(
              awsObjectKey,
              fileMeta,
              file,
              bucket,
              objectName,
              onComplete,
              onError,
              uploadUrl
            )
          },
          error: (mssg) => {
            console.log('Error', mssg)
            onError?.(mssg, file)
          },
          paused: () => console.log('Paused'),
          pausing: () => console.log('Pausing'),
          resumed: () => console.log('Resumed'),
          cancelled: () => {
            console.log('Cancelled')
            onCancel?.(file)
          },
          warn: (mssg) => console.log('Warning', mssg),
          progress: (percent, stats) => {
            const total = stats.fileSize
            const done = stats.totalUploaded
            let percentage = Math.floor((done * 100) / total)
            const remaining_s = stats.secondsLeft

            // We show 4% as the minimum progress bar width
            // Once this is complete we upload the file to S3 and it takes some time, so limit the progress to 95%
            if (percentage > 4) {
              percentage = Math.min(percentage, 95)
            }

            const remain = calculateRemain(
              total,
              done,
              remaining_s,
              uploadStartedAt
            )

            onProgress?.({
              percentage,
              total,
              done,
              remaining: remain,
              file,
            })
          },
        })

        return {
          promise: uploadPromise,
          cancel: () => evaporate.cancel(bucket + '/' + fullPath),
        }
      } catch (error) {
        onError?.(error.message, file)
        throw error
      }
    },
  }
}

function handleUploadComplete(
  awsObjectKey,
  fileMeta,
  file,
  bucket,
  objectName,
  onComplete,
  onError,
  uploadUrl
) {
  import('~/common/helpers/csrf').then(({ getCsrfParams }) => {
    awsObjectKey = decodeURIComponent(awsObjectKey)
    let [param, value] = getCsrfParams()

    let postData = {
      s3_key: awsObjectKey,
      s3_bucket: bucket,
      filename: fileMeta.name,
      size: fileMeta.size,
      width: fileMeta.width,
      height: fileMeta.height,
      // Browser MIME so audio-only .mp4 voice memos can be created as AudioAsset
      content_type: file.type || undefined,
      object_name: objectName,
    }

    postData[param] = value

    const xhr = new XMLHttpRequest()
    xhr.onreadystatechange = function () {
      if (this.readyState == 4) {
        let response
        try {
          response = JSON.parse(xhr.responseText)
        } catch (e) {
          response = { error: 'Error in processing the file' }
          console.log('error in XHTTP', xhr)
        }

        if (this.status == 200) {
          onComplete?.(response, file)
        } else {
          onError?.(response.error, file)
        }
      }
    }

    xhr.open('POST', uploadUrl, true)
    xhr.setRequestHeader('Content-Type', 'application/json;charset=UTF-8')
    xhr.send(JSON.stringify(postData))
  })
}
